import { useState, useRef } from 'react';
import { Key, Mail, Phone, Loader2, ArrowLeft, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { normalizePhoneToE164, isValidPhoneNumber } from '@/lib/phoneUtils';
import { startBankIdFlow } from '@/lib/bankid';

type LoginMethod = 'email' | 'phone' | 'bankid' | 'invite';

const ALL_METHODS: LoginMethod[] = ['email', 'phone', 'bankid', 'invite'];

interface LoginPageProps {
  orgName?: string;
  orgSlug?: string;
  orgLogoUrl?: string;
  /** Which login methods to show. Undefined = all methods. */
  allowedMethods?: string[];
  /** Which method to highlight/show first. */
  primaryMethod?: string;
  /** Org primary color (hex) — applied as CSS variable on the root container. */
  primaryColor?: string;
  /**
   * True when the page renders above a custom org background (image/video/gradient).
   * Enables the glass surface treatment so text stays readable on arbitrary backdrops.
   */
  hasCustomBackground?: boolean;
  /**
   * Custom landing headline authored per org/device in the CMS.
   * Falls back to the org name when unset.
   */
  headline?: string;
  /**
   * Custom landing subheadline authored per org/device in the CMS.
   * Falls back to the default login copy when unset.
   */
  subheadline?: string;
  /** Hide the logo when the CMS `show_logo` toggle is off. Defaults to shown. */
  showLogo?: boolean;
  /** Hide the headline when the CMS `show_headline` toggle is off. Defaults to shown. */
  showHeadline?: boolean;
  /** Hide the subheadline when the CMS `show_subheadline` toggle is off. Defaults to shown. */
  showSubheadline?: boolean;
  /** If set, show invite code input instead of normal login */
  inviteMode?: boolean;
  /** If set, show "Be om åtkomst" intercom button */
  onIntercomClick?: () => void;
}

type LoginStep = 'choose' | 'email' | 'email-otp' | 'phone' | 'phone-otp' | 'invite';

export function LoginPage({
  orgName,
  orgLogoUrl,
  allowedMethods,
  primaryMethod,
  primaryColor,
  hasCustomBackground = false,
  headline,
  subheadline,
  showLogo = true,
  showHeadline = true,
  showSubheadline = true,
  inviteMode = false,
  onIntercomClick,
}: LoginPageProps) {
  const methods: LoginMethod[] = allowedMethods
    ? ALL_METHODS.filter(m => allowedMethods.includes(m))
    : ALL_METHODS;

  // If only one method, skip the choose step and go directly to it
  const singleMethod = methods.length === 1 ? methods[0] : null;
  const initialStep: LoginStep = inviteMode
    ? 'invite'
    : singleMethod === 'email'
      ? 'email'
      : singleMethod === 'phone'
        ? 'phone'
        : singleMethod === 'invite'
          ? 'invite'
          : 'choose';

  const [step, setStep] = useState<LoginStep>(initialStep);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
    });

    setLoading(false);

    if (authError) {
      setError('Kunde inte skicka verifieringskod. Försök igen.');
    } else {
      setOtpCode('');
      setStep('email-otp');
    }
  };

  const handleVerifyEmailOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode.trim()) return;

    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: otpCode.trim(),
      type: 'email',
    });

    setLoading(false);

    if (authError) {
      setError('Felaktig kod. Kontrollera och försök igen.');
    }
    // On success, AuthContext will detect the session change
  };

  const bankIdLockRef = useRef(false);

  const handleBankID = async () => {
    // Prevent duplicate init calls (race condition guard)
    if (bankIdLockRef.current) return;
    bankIdLockRef.current = true;

    setLoading(true);
    setError(null);

    try {
      const result = await startBankIdFlow();
      if (!result.ok) {
        setError(result.message);
        setLoading(false);
      }
    } finally {
      setTimeout(() => {
        bankIdLockRef.current = false;
      }, 1500);
    }
  };

  const getPhoneE164 = () => {
    const digits = phone.replace(/\D/g, '');
    if (!digits) return '';
    // User enters local number without country code; prepend +46
    return normalizePhoneToE164(digits, 'SE');
  };

  const handlePhoneSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const phoneE164 = getPhoneE164();
    if (!phoneE164) return;

    if (!isValidPhoneNumber(phoneE164)) {
      setError('Ogiltigt telefonnummer. Ange ditt svenska mobilnummer.');
      return;
    }

    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.signInWithOtp({
      phone: phoneE164,
    });

    setLoading(false);

    if (authError) {
      setError('Kunde inte skicka SMS-kod. Kontrollera numret och försök igen.');
    } else {
      setStep('phone-otp');
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode.trim()) return;

    setLoading(true);
    setError(null);

    const { error: authError } = await supabase.auth.verifyOtp({
      phone: getPhoneE164(),
      token: otpCode.trim(),
      type: 'sms',
    });

    setLoading(false);

    if (authError) {
      setError('Felaktig kod. Kontrollera och försök igen.');
    }
    // On success, AuthContext will detect the session change
  };

  const handleInviteCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setLoading(true);
    setError(null);

    // Invite codes are validated server-side via Edge Function
    const { data, error: fnError } = await supabase.functions.invoke(
      'api-access-control',
      {
        body: JSON.stringify({
          action: 'redeem-invite',
          invite_code: inviteCode.trim(),
        }),
      }
    );

    setLoading(false);

    if (fnError || !data) {
      setError('Ogiltig åtkomstkod. Kontrollera koden och försök igen.');
    } else {
      // Invite redeemed — redirect to email login with pre-filled email
      if (data.email) {
        setEmail(data.email);
      }
      setStep('email');
    }
  };

  // When rendered above a custom org background, lift the text and card onto
  // a glass surface so copy stays readable regardless of the backdrop.
  const titleClass = hasCustomBackground
    ? 'text-2xl font-bold tracking-tight text-white drop-shadow'
    : 'text-2xl font-bold tracking-tight';
  const subtitleClass = hasCustomBackground
    ? 'text-sm text-white/80 mt-1 drop-shadow'
    : 'text-sm text-muted-foreground mt-1';

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center p-6 relative"
      style={primaryColor ? ({ ['--primary' as any]: primaryColor } as React.CSSProperties) : undefined}
    >
      <div className="w-full max-w-sm space-y-6 relative z-10">
        {/* Logo & Title */}
        <div className="text-center space-y-3">
          {showLogo && (
            orgLogoUrl ? (
              <img
                src={orgLogoUrl}
                alt={orgName || 'Organisation'}
                className="mx-auto h-16 w-auto max-w-[180px] object-contain"
              />
            ) : (
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl glass-regular">
                <Key className="h-8 w-8 text-foreground" />
              </div>
            )
          )}
          {(showHeadline || showSubheadline) && (
            <div>
              {showHeadline && (
                <h1 className={titleClass}>
                  {headline || orgName || 'ZipLink Åtkomst'}
                </h1>
              )}
              {showSubheadline && (
                <p className={subtitleClass}>
                  {step === 'invite'
                    ? 'Ange din åtkomstkod'
                    : subheadline || 'Logga in för att hantera dina nycklar'}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive animate-scale-in">
            {error}
          </div>
        )}

        {/* Step: Choose method */}
        {step === 'choose' && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6 space-y-3">
              {methods.includes('email') && (
                <Button
                  className="w-full"
                  size="lg"
                  variant={primaryMethod === 'email' ? 'default' : methods[0] === 'email' ? 'default' : 'outline'}
                  onClick={() => setStep('email')}
                >
                  <Mail className="h-5 w-5" />
                  Logga in med e-post
                </Button>
              )}

              {methods.includes('phone') && (
                <Button
                  className="w-full"
                  size="lg"
                  variant={primaryMethod === 'phone' ? 'default' : 'outline'}
                  onClick={() => setStep('phone')}
                >
                  <Phone className="h-5 w-5" />
                  Logga in med telefon
                </Button>
              )}

              {methods.includes('bankid') && (
                <Button
                  className="w-full"
                  size="lg"
                  variant={primaryMethod === 'bankid' || methods[0] === 'bankid' ? 'default' : 'outline'}
                  onClick={handleBankID}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Logga in med BankID'
                  )}
                </Button>
              )}

              {(methods.includes('invite') || onIntercomClick) && (
                <>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        eller
                      </span>
                    </div>
                  </div>

                  {methods.includes('invite') && (
                    <Button
                      className="w-full"
                      size="lg"
                      variant="ghost"
                      onClick={() => setStep('invite')}
                    >
                      Jag har en åtkomstkod
                    </Button>
                  )}

                  {onIntercomClick && (
                    <Button
                      className="w-full"
                      size="lg"
                      variant="ghost"
                      onClick={onIntercomClick}
                    >
                      <Bell className="h-5 w-5" />
                      Be om åtkomst
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step: Email login */}
        {step === 'email' && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6">
              <form onSubmit={handleEmailSendOtp} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    E-postadress
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="namn@exempel.se"
                    required
                    autoFocus
                    className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || !email.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Mail className="h-5 w-5" />
                      Skicka kod
                    </>
                  )}
                </Button>

                {!singleMethod && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setStep('choose')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Tillbaka
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step: Email OTP verification */}
        {step === 'email-otp' && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6">
              <form onSubmit={handleVerifyEmailOtp} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email-otp-input" className="text-sm font-medium">
                    Verifieringskod
                  </label>
                  <input
                    id="email-otp-input"
                    type="text"
                    inputMode="numeric"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                    autoFocus
                    maxLength={6}
                    className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-center text-xl font-mono tracking-widest ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Ange den 6-siffriga koden som skickades till{' '}
                    <span className="font-medium text-foreground">{email}</span>
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || otpCode.length < 6}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Verifiera'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setOtpCode('');
                    setError(null);
                    setStep('email');
                  }}
                >
                  Skicka ny kod
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step: Phone login */}
        {step === 'phone' && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6">
              <form onSubmit={handlePhoneSend} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="phone" className="text-sm font-medium">
                    Telefonnummer
                  </label>
                  <div className="flex items-center gap-0">
                    <span className="flex h-10 items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground select-none">
                      +46
                    </span>
                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="70 123 45 67"
                      required
                      autoFocus
                      className="flex h-10 w-full rounded-r-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || !phone.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Phone className="h-5 w-5" />
                      Skicka SMS-kod
                    </>
                  )}
                </Button>

                {!singleMethod && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setStep('choose')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Tillbaka
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step: Phone OTP verification */}
        {step === 'phone-otp' && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6">
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="otp" className="text-sm font-medium">
                    Verifieringskod
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                    autoFocus
                    maxLength={6}
                    className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-center text-xl font-mono tracking-widest ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Ange den 6-siffriga koden som skickades till{' '}
                    <span className="font-medium text-foreground">{phone}</span>
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || otpCode.length < 6}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Verifiera'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setOtpCode('');
                    setError(null);
                    setStep('phone');
                  }}
                >
                  Skicka ny kod
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step: Invite code */}
        {step === 'invite' && (
          <Card className="animate-fade-in">
            <CardContent className="pt-6">
              <form onSubmit={handleInviteCode} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="invite" className="text-sm font-medium">
                    Åtkomstkod
                  </label>
                  <input
                    id="invite"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="ABC123"
                    required
                    autoFocus
                    maxLength={12}
                    className="flex h-12 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-center text-xl font-mono tracking-widest ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Du fick koden från din värd eller administratör
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || !inviteCode.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Verifiera kod'
                  )}
                </Button>

                {!singleMethod && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => setStep('choose')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Tillbaka
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
